import React from "react";
import { fmtDuration } from "../../shared/format";
import { intelOf, labelFor } from "./conversationIntel";

// Voice Intelligence — Phase 7 row content. Renders under the conversation name
// in the worklist: outcome badge, stay-type chip, duration, a play icon when a
// recording exists, and a marker when the caller never spoke.
//
// Render-only. Every value comes from fn_get_conversation_list merged onto the
// row; nothing here derives or infers. A row with no intelligence record renders
// nothing at all rather than a row of dashes — same rule as the detail pane.

// Outcome carries the one bit of colour in the row, because it is the field a
// reader scans for. Abandoned and wrong-number are the bad outcomes; the two
// reservation outcomes are the good ones; everything else is neutral so the row
// does not turn into a traffic-light wash.
const OUTCOME_TONE = {
  reservation_made: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800",
  reservation_assisted: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800",
  abandoned: "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800",
  wrong_number: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700",
};
const NEUTRAL_TONE =
  "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700";

const Chip = ({ children, className = "", title }) => (
  <span
    title={title}
    className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border shrink-0 ${className}`}
  >
    {children}
  </span>
);

const PlayIcon = () => (
  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const RowIntel = ({ row }) => {
  const it = intelOf(row);
  const hasAny =
    it.outcome ||
    it.stay_type_sought ||
    it.has_recording ||
    it.caller_ever_spoke === false ||
    it.duration_seconds != null;
  if (!hasAny) return null;

  // `unknown` is a stored classification, not an absence, but it tells a reader
  // nothing in a dense list — so it is dropped from the row and kept in the
  // filters, where isolating those rows is the point.
  const stayType = it.stay_type_sought && it.stay_type_sought !== "unknown" ? it.stay_type_sought : null;

  return (
    <div className="flex items-center flex-wrap gap-1 mt-1" data-testid="row-intel">
      {it.outcome && (
        <Chip className={OUTCOME_TONE[it.outcome] || NEUTRAL_TONE} title={`Outcome: ${labelFor(it.outcome)}`}>
          {labelFor(it.outcome)}
        </Chip>
      )}
      {stayType && <Chip className={NEUTRAL_TONE} title={`Stay type: ${labelFor(stayType)}`}>{labelFor(stayType)}</Chip>}
      {it.duration_seconds != null && (
        <span className="text-[10px] text-gray-500 dark:text-gray-400 shrink-0">{fmtDuration(it.duration_seconds)}</span>
      )}
      {it.has_recording && (
        <span
          className="inline-flex items-center text-gray-500 dark:text-gray-400 shrink-0"
          title="Recording available"
          aria-label="Recording available"
          data-testid="row-play-icon"
        >
          <PlayIcon />
        </span>
      )}
      {/* Only false is a finding. null means the call was never classified, and
          claiming "never spoke" from missing data would invent the very thing
          the greeting-loss panel measures. */}
      {it.caller_ever_spoke === false && (
        <Chip
          className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-800"
          title="The caller hung up during the greeting"
        >
          Never spoke
        </Chip>
      )}
    </div>
  );
};

export default RowIntel;
